// 환자 자동관리 ① 「먼저 확인할 것」 — 원천 4종 병합·정렬 순수 함수 (2026-10-07, 기획서_환자자동관리_통합화면 v3 §3-1 · 법무검토 10-07 P2 반영)
// firebase 의존 없음 — 단위 테스트 대상(src/utils/__tests__/patientsFirstList.test.js).
//
// 원천 → 사유:
//   질문 답 sections.first_look / to_check (파일럿만)            → 'first_look' / 'to_check'   k = answeredAt
//   보드 last7 rows, event==='skipped', pid+kind 당 최신 1행      → 'undelivered'               k = at        사유 라벨 = 서버 eventLabel('보내지 못함') 그대로(법무 P2-1)
//   보드 clinicPhoneMissing === true (1행, 환자명 없음)            → 'phone_missing'             k = generatedAt(지금)  사유 배지 「대표전화 미등록」(법무 P2-2)
//   명부 status 'deleteRequested' (기한 = deleteRequestedAt+10일)  → 'delete_requested'          k = 기한 − 10일
//   명부 status 'unconfirmed'     (기한 = createdAt+30일)          → 'unconfirmed'               k = 기한 − 10일
// 정렬 = k 오름차순(오래된 것·기한 가까운 것이 위). 동률 = 사유 우선순위 → 환자명 가나다 → 안정 키.
// D-n = ceil((기한 − 오늘 00:00) / 1일). 오늘 = 서버 todayKey(KST). 보드 전부 실패 시 PC 시계(각주 표시 — pcClock:true).
// 금지(법무 RM §2-3): 점수·등급·경보색·결론 라벨을 여기서 만들지 않는다. D 값에 따른 색 분기 0.

export const DAY_MS = 24 * 60 * 60 * 1000;
export const DELETE_REQUEST_DEADLINE_MS = 10 * DAY_MS;    // 삭제 요청 처리 기한(정본 v2.1-6, 서버 clinic-patients.js DELETE_REQUEST_DEADLINE_MS 와 같은 값)
export const UNCONFIRMED_DEADLINE_MS = 30 * DAY_MS;       // 이전 방식 등록 자동 파기(clinic-patient-self-register.js unconfirmed_30d)
export const FIRST_AXIS_WINDOW_MS = DELETE_REQUEST_DEADLINE_MS;   // 기한형 항목의 정렬키 = 기한 − 이 값

export const REASON = Object.freeze({
    FIRST_LOOK: 'first_look', DELETE_REQUESTED: 'delete_requested', UNDELIVERED: 'undelivered', PHONE_MISSING: 'phone_missing', TO_CHECK: 'to_check', UNCONFIRMED: 'unconfirmed',
});
// 동률 시 사유 우선순위(기획 §3-1) — 대표전화 미등록은 「전달 안 됨」 계열과 같은 자리
export const REASON_PRIORITY = Object.freeze({ first_look: 0, delete_requested: 1, undelivered: 2, phone_missing: 2, to_check: 3, unconfirmed: 4 });

// ☆ 웹 신설 문구(법무 10-07 §1 승인분) — 사유 배지·동작·내용
export const TEXT = Object.freeze({
    firstLookFallback: '먼저 볼 답',       // 응답 labels.firstLook 이 없을 때만
    toCheckFallback: '확인할 답',          // 응답 labels.toCheck 이 없을 때만
    undeliveredFallback: '보내지 못함',    // 행 eventLabel 이 없을 때만(서버 EVENT_LABELS.skipped 와 같은 문자열)
    phoneMissing: '대표전화 미등록',       // 사유 배지(법무 P2-2)
    phoneMissingContent: '치과 대표전화 미등록',   // 내용 열 — 서버 RESULT_LABELS.no_contact 와 같은 문자열(보드 응답에 사전이 없어 웹에 둠)
    deleteRequested: '삭제 요청',
    unconfirmed: '이전 방식 등록',
    daysLeft: (n) => `D-${n} 남음`,
    expired: '기한 지남',                  // ☆9 — 서버 S8 이 이미 삭제했어야 할 상태
    actionDesk: '데스크에서 직접 안내',    // ☆6
    actionDetail: '환자 상세에서 전달 결과 확인',   // ☆7
    actionSettings: '설정에서 확인',       // ☆8
    actionOwnerDelete: '상세에서 삭제 처리',
    actionStaffDelete: '치과 대표가 처리',
    deleteContent: (reasonLabel, deadline) => `${reasonLabel} · 기한 ${deadline} (요청일부터 10일 · 지나면 자동 삭제)`,   // 법무 P2-3
    unconfirmedContent: (deadline) => `이전 방식 등록 · 기한 ${deadline} (지나면 자동 삭제)`,
    emptyPilot: '지금 확인할 것이 없습니다.',   // ☆4
    emptyBasic: '지금 확인할 것이 없습니다. 전달되지 못한 안내와 기한 있는 요청이 생기면 여기에 보입니다.',   // ☆5
    sortHint: '오래된 것·기한이 가까운 것부터',
    pcClockNote: '(오늘 날짜는 이 PC의 시계 기준입니다. 실제 삭제는 서버 날짜로 처리됩니다)',   // 법무 P2-6
    partialError: '일부 소스를 불러오지 못했습니다',   // 서버 texts.partialError 와 동일 문자열
});

// 전달 안 됨 행 「동작」 분기 — 서버 RESULT_LABELS 문자열 집합 대조(기획 §8-2: 서버 문구가 바뀌면 기본값으로 떨어질 뿐 깨지지 않음)
const DESK_LABELS = new Set([
    '알림 꺼짐 — 앱 안 카드로만 전달', '앱 없음(알림 받을 기기 없음)', '확인된 기기 없음(앱 기기 확인 전)',
    '질문·안내 받기 동의 없음', '질문·안내 받기 동의 만료', '질문·안내 받기 동의 없음(문안 미확정)',
    '알림 수신 꺼짐', '재내원 안내 수신 꺼짐',
    '서류 받기 동의 없음', '재내원 안내 동의 없음', '장착 후 확인 동의 없음', '진료 후 질문 동의 없음',
]);
const SETTINGS_LABELS = new Set(['치과 대표전화 미등록', '치과명 미등록', '치과명에 쓸 수 없는 표현이 있어 보류(고객센터 문의)', '위탁 재확인 필요']);
export function undeliveredActionOf(resultLabel) {
    if (DESK_LABELS.has(resultLabel)) return TEXT.actionDesk;
    if (SETTINGS_LABELS.has(resultLabel)) return TEXT.actionSettings;
    return TEXT.actionDetail;
}

const pad = (n) => String(n).padStart(2, '0');
export const fmtDate = (d) => {
    if (!d) return '';
    const x = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(x.getTime())) return '';
    return `${x.getFullYear()}.${pad(x.getMonth() + 1)}.${pad(x.getDate())}`;
};
export const fmtDateTime = (ms) => {
    if (!Number.isFinite(ms)) return '';
    const x = new Date(ms);
    return `${fmtDate(x)} ${pad(x.getHours())}:${pad(x.getMinutes())}`;
};
const toMs = (v) => {
    if (v == null) return null;
    if (v instanceof Date) { const t = v.getTime(); return Number.isNaN(t) ? null : t; }
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;
    if (typeof v === 'object' && typeof v._seconds === 'number') return v._seconds * 1000;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
};

// 서버 todayKey('YYYY-MM-DD', KST) → 그날 00:00 KST(ms). 형식이 아니면 null
export function todayStartOf(todayKey) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(todayKey || ''))) return null;
    const t = Date.parse(`${todayKey}T00:00:00+09:00`);
    return Number.isFinite(t) ? t : null;
}
// PC 시계 기준 오늘 00:00(로컬)
export function localTodayStart(nowMs) {
    const d = new Date(nowMs);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}
// 남은 날 = floor((기한 − 오늘 00:00) / 1일). 기한이 오늘 안(00:00~23:59)이면 0(= "D-0 남음"), 내일이면 1, 어제면 −1(기한 지남)
//   ☆ 기획 §3-1 은 ceil 로 적었으나 기한에 시각(요청 시각+10일)이 붙어 있어 ceil 은 당일 기한을 D-1 로 보인다 — "기한일 당일 = D-0" 의도에 맞춰 floor 채택
export function daysLeftOf(deadlineMs, todayStartMs) {
    return Math.floor((deadlineMs - todayStartMs) / DAY_MS);
}
export const daysLeftLabel = (n) => (n < 0 ? TEXT.expired : TEXT.daysLeft(n));

export function compareFirstRows(a, b) {
    if (a.k !== b.k) return a.k < b.k ? -1 : 1;
    const pa = REASON_PRIORITY[a.reason] ?? 9, pb = REASON_PRIORITY[b.reason] ?? 9;
    if (pa !== pb) return pa - pb;
    const n = String(a.name || '').localeCompare(String(b.name || ''), 'ko');
    if (n !== 0) return n;
    return String(a.key).localeCompare(String(b.key));
}

/**
 * @param {object} p
 * @param {object|null} p.answers       listClinicRoadmapAnswers 응답(sections·labels). 비파일럿이면 null
 * @param {Array}  p.last7Rows          보드 last7 rows
 * @param {boolean} p.clinicPhoneMissing 보드 응답 clinicPhoneMissing
 * @param {number|null} p.generatedAt   보드 응답 generatedAt(ms)
 * @param {Array}  p.records            명부 rows(normalizeRecord 결과)
 * @param {string|null} p.todayKey      보드 응답 todayKey(서버 KST). 없으면 PC 시계
 * @param {number} p.nowMs
 * @param {boolean} p.roadmapOn         false 면 kind 'roadmap' 전달 안 됨 행 제거(법무 P2-7)
 * @returns {{ rows: Array, pcClock: boolean, expiredCount: number }}
 */
export function buildFirstRows({ answers, last7Rows, clinicPhoneMissing, generatedAt, records, todayKey, nowMs, roadmapOn }) {
    const now = Number.isFinite(nowMs) ? nowMs : Date.now();
    const serverToday = todayStartOf(todayKey);
    const pcClock = serverToday == null;
    const todayStart = pcClock ? localTodayStart(now) : serverToday;
    const rows = [];
    let expiredCount = 0;

    // ── 질문 답(파일럿만) ──
    if (answers && answers.sections) {
        const labels = answers.labels || {};
        const push = (bucket, reason, label) => {
            for (const r of answers.sections[bucket] || []) {
                if (!r || !r.fbId) continue;
                const k = toMs(r.answeredAt);
                rows.push({
                    key: `a:${r.fbId}`, reason, reasonLabel: label, k: k == null ? now : k,
                    pid: r.pid || '', name: r.name || '', src: r,
                });
            }
        };
        push('first_look', REASON.FIRST_LOOK, labels.firstLook || TEXT.firstLookFallback);
        push('to_check', REASON.TO_CHECK, labels.toCheck || TEXT.toCheckFallback);
    }

    // ── 전달 안 됨(last7 skipped, pid+kind 최신 1행) ──
    const latest = new Map();
    for (const r of last7Rows || []) {
        if (!r || r.event !== 'skipped' || !r.pid) continue;
        if (roadmapOn === false && r.kind === 'roadmap') continue;
        const key = `${r.pid}|${r.kind}`;
        const at = toMs(r.at);
        const prev = latest.get(key);
        if (!prev || (at != null && (prev.at == null || at > prev.at))) latest.set(key, { ...r, at });
    }
    for (const r of latest.values()) {
        rows.push({
            key: `u:${r.pid}|${r.kind}|${r.refId || ''}`, reason: REASON.UNDELIVERED, reasonLabel: r.eventLabel || TEXT.undeliveredFallback,
            k: r.at == null ? now : r.at, pid: r.pid, name: r.name || '', src: r,
            content: r.label || '', contentSub: r.stageLabel || '', answer: r.resultLabel || '',
            when: r.at == null ? '' : fmtDateTime(r.at), action: undeliveredActionOf(r.resultLabel || ''),
        });
    }

    // ── 대표전화 미등록(1행) ──
    if (clinicPhoneMissing === true) {
        const k = toMs(generatedAt);
        rows.push({
            key: 'p:clinicPhoneMissing', reason: REASON.PHONE_MISSING, reasonLabel: TEXT.phoneMissing, k: k == null ? now : k,
            pid: '', name: '', src: null,
            content: TEXT.phoneMissingContent, contentSub: '', answer: '', when: '', action: TEXT.actionSettings,
        });
    }

    // ── 명부 기한형 ──
    for (const r of records || []) {
        if (!r || r.legacy) continue;
        if (r.status === 'deleteRequested') {
            const base = toMs(r.deleteRequestedAt) ?? toMs(r.createdAt) ?? now;   // ☆ deleteRequestedAt 이 없으면 등록일로 대체(구버전 레코드)
            const deadline = base + DELETE_REQUEST_DEADLINE_MS;
            const n = daysLeftOf(deadline, todayStart);
            if (n < 0) expiredCount += 1;
            rows.push({
                key: `d:${r.id}`, reason: REASON.DELETE_REQUESTED, reasonLabel: TEXT.deleteRequested, k: deadline - FIRST_AXIS_WINDOW_MS,
                pid: r.id, name: r.name || '', src: r, daysLeft: n, daysLeftLabel: daysLeftLabel(n), deadline,
                content: null,   // 사유 라벨은 화면에서 deleteRequestReasonLabel(r.deleteRequestReason) 로 채움(함수 출력 그대로 — 법무 P2-9)
                when: r.deleteRequestedAt ? `요청 ${fmtDate(r.deleteRequestedAt)}` : '',
            });
        } else if (r.status === 'unconfirmed') {
            const base = toMs(r.createdAt) ?? now;
            const deadline = base + UNCONFIRMED_DEADLINE_MS;
            const n = daysLeftOf(deadline, todayStart);
            if (n < 0) expiredCount += 1;
            rows.push({
                key: `c:${r.id}`, reason: REASON.UNCONFIRMED, reasonLabel: TEXT.unconfirmed, k: deadline - FIRST_AXIS_WINDOW_MS,
                pid: r.id, name: r.name || '', src: r, daysLeft: n, daysLeftLabel: daysLeftLabel(n), deadline,
                content: TEXT.unconfirmedContent(fmtDate(new Date(deadline))), contentSub: '', answer: '',
                when: r.createdAt ? `등록 ${fmtDate(r.createdAt)}` : '',
            });
        }
    }

    rows.sort(compareFirstRows);
    return { rows, pcClock, expiredCount };
}

export default buildFirstRows;
